
import { IView, UserListItem } from "./types";
import { GlobalState } from "./globalstate";
import { VnodeAttrs, Component, m, ComponentFunc, EventHandler, VnodeDOM, VnodeChildren } from "./mithril";
import { AnimatedNetskraflLogo } from "./logo";
import { buttonOut, buttonOver, glyph, nbsp, playAudio } from "./util";
import { ts } from "./i18n";
import { request } from "./request";

const SPINNER_INITIAL_DELAY = 800; // milliseconds

export const Spinner: Component<
  {},
  {
    ival: ReturnType<typeof setTimeout> | number;
    show: boolean;
  }
> = {
  // Show a spinner wait box, after an initial delay
  oninit: (vnode) => {
    vnode.state.show = false;
    vnode.state.ival = setTimeout(() => {
      vnode.state.show = true; vnode.state.ival = 0; m.redraw();
    }, SPINNER_INITIAL_DELAY);
  },
  onremove: (vnode) => {
    if (vnode.state.ival)
      clearTimeout(vnode.state.ival);
    vnode.state.ival = 0;
  },
  view: (vnode) => {
    if (!vnode.state.show)
      return undefined;
    return m(
      ".modal-dialog",
      { id: 'spinner-dialog', style: { visibility: 'visible' } },
      m(".animated-spinner",
        m(AnimatedNetskraflLogo, { withCircle: true })
      )
    );
  }
};

export const TextInput: ComponentFunc<{
  initialValue: string;
  class: string;
  maxlength: number;
  id: string;
  tabindex?: number;
  autocomplete?: string;
}> = () => {

  // Generic text input field

  let value = "";
  let cls = "";

  return {
    oninit: (vnode) => {
      value = vnode.attrs.initialValue + "";
      cls = vnode.attrs.class;
      if (cls)
        cls = "." + cls.split(" ").join(".");
      else
        cls = "";
    },
    view: (vnode) => {
      const { id, maxlength, tabindex, autocomplete } = vnode.attrs;
      return m("input.text" + cls,
        {
          id,
          name: id,
          maxlength,
          tabindex,
          autocomplete,
          value,
          oninput: (ev: Event) => { value = (ev.target as HTMLInputElement).value + ""; }
        }
      );
    }
  };

}

export const DialogButton: Component<{
  id: string;
  title: string;
  onclick: EventHandler;
  tabindex?: number;
}> = {
  view: (vnode) => {
    const attrs: VnodeAttrs = {
      onmouseout: buttonOut,
      onmouseover: buttonOver
    };
    Object.assign(attrs, vnode.attrs);
    return m(".modal-close", attrs, vnode.children);
  }
};

export const ChallengeButton: Component<{
  view: IView;
  item: UserListItem;
}> = {
  view: (vnode) => {
    const item = vnode.attrs.item;
    const outerView = vnode.attrs.view;
    const actions = outerView.actions;
    const isRobot = item.userid.indexOf("robot-") === 0;

    function modifyChallenge() {
      if (item.chall) {
        // Retracting challenge
        item.chall = false;
        // Note: the effect of this is to retract all challenges
        // that this user has issued to the destination user
        actions.retractChallenge(item.userid, "");
      }
      else if (isRobot) {
          // Challenging a robot: game starts immediately
          actions.startRobotGame(item.userid);
      } else {
          // Challenging a user: show a challenge dialog
          outerView.pushDialog("challenge", item);
      }
    }

    return m("span.list-ch",
      {
        title: ts("Skora á"),
        onclick: (ev: Event) => {
          modifyChallenge();
          ev.preventDefault();
        }
      },
      glyph("hand-right", undefined, !item.chall),
    );
  }
};

export const UserInfoButton: Component<{
  view: IView;
  item: { userid: string; nick: string; fullname: string; };
}> = {
  view: (vnode) => {
    const item = vnode.attrs.item;
    const outerView = vnode.attrs.view;
    const isRobot = !item.userid || item.userid.indexOf("robot-") === 0;
    return m("span.list-info",
      {
        title: ts("Skoða feril"),
        // Show opponent track record
        onclick: (ev: Event) => {
          if (!isRobot)
            outerView.showUserInfo(item.userid, item.nick, item.fullname);
          ev.preventDefault();
        }
      },
      isRobot ? "" : m("span.usr-info"),
    );
  }
};

interface IOnlinePresenceState {
  online: boolean;
}

interface IOnlinePresenceAttributes {
  id: string;
  userId: string;
  state: GlobalState;
  online?: boolean;
}

export const OnlinePresence: Component<IOnlinePresenceAttributes, IOnlinePresenceState> = {

  // Shows an icon in grey or green depending on whether a given user
  // is online or not. If attrs.online is given (i.e. not undefined),
  // that value is used and displayed; otherwise the server is asked.

  oninit: (vnode) => {
    const { state } = vnode;
    state.online = false;
  },

  oncreate: (vnode) => {
    // Note: we must use a two-step initialization here,
    // calling oninit() first and then oncreate(), since calls to
    // m.redraw() - explicit or implicit via m.request() - from within
    // oninit() will cause a recursive call to oninit(), resulting in
    // a double API call to the backend.
    const { attrs, state } = vnode;
    state.online = !!attrs.online;
    if (attrs.online === undefined) {
      // We need to ask the server for the online status
      const askServer = async () => {
        try {
          const json = await request<{ online: boolean; }>(attrs.state, {
            method: "POST",
            url: "/onlinecheck",
            body: { user: attrs.userId }
          });
          state.online = json && json.online;
        }
        catch(e) {
          state.online = false;
        }
      };
      askServer(); // Fire-and-forget
    }
  },

  view: (vnode) => {
    const { attrs, state } = vnode;
    const online = state.online || (!!attrs.online);
    return m("span",
      {
        id: attrs.id,
        title: online ? ts("Er álínis") : ts("Álínis?"),
        class: online ? "online" : ""
      }
    );
  }

};

export const UserId: Component<{ view: IView }> = {
  // User identifier at top right, opens user preferences
  view: (vnode) => {
    const view = vnode.attrs.view;
    const model = view.model;
    if (!model.state?.netskraflUserId)
      // Don't show the button if there is no logged-in user
      return "";
    return m(".userid",
      {
        title: ts("player_info"), // "Player information"
        onclick: (ev: Event) => {
          // Overlay the userprefs dialog
          view.pushDialog("userprefs");
          ev.preventDefault();
        }
      },
      [glyph("address-book"), nbsp(), model.state.userNick]
    );
  }
};

export const ProModeIndicator: Component<{
  manual?: boolean;
  header?: boolean;
}> = {
  // Display a Pro Mode (Keppnishamur) indicator using a lightbulb glyph
  // If header is true, always show grayed with title
  // Otherwise, the lightbulb is grayed if manual mode is not enabled
  view: (vnode) => {
    const { manual, header } = vnode.attrs;
    const title = ts("Keppnishamur");
    // Regular case: show grayed if not manual mode
    const highlight = (manual === true) && !header;
    const glyphAttrs = (highlight || header) ? { title } : {};
    return m("span.list-manual",
      glyphAttrs,
      glyph("lightbulb", undefined, !highlight), // Grayed if not highlighted
    );
  }
};

export const MultiSelection: ComponentFunc<{
  initialSelection?: number;
  defaultClass?: string;
  selectedClass?: string;
}> = (initialVnode) => {

  // A multiple-selection div where users can click on child nodes
  // to select them, giving them an addional selection class,
  // typically .selected

  let sel = initialVnode.attrs.initialSelection ?? 0;
  const defaultClass = initialVnode.attrs.defaultClass ?? "";
  const selectedClass = initialVnode.attrs.selectedClass || "selected";

  return {
    view: (vnode) => {
      const children = vnode.children;
      if (!Array.isArray(children)) return undefined;
      return m("div",
        {
          onclick: (ev: Event) => {
            // Catch clicks that are propagated from children up
            // to the parent div. Find which child originated the
            // click (possibly in descendant nodes) and set
            // the current selection accordingly.
            const childNodes = children.map((c) => (c as VnodeDOM).dom);
            for (let i = 0; i < childNodes.length; i++)
              if (childNodes[i].contains(ev.target as Node))
                sel = i;
            ev.stopPropagation();
          }
        },
        children.map((item, i) => {
          // A pretty gross approach, but it works: clobber the childrens' className
          // attribute depending on whether they are selected or not
          const it = item as m.Vnode<{ className: string }>;
          if (i === sel)
            it.attrs.className = defaultClass + " " + selectedClass;
          else
            it.attrs.className = defaultClass;
          return item;
        })
      );
    }
  };

};

export const Info: Component = {
  // Info icon, invoking the help screen
  view: () => m(".info",
    { title: ts("Upplýsingar og hjálp") },
    m(m.route.Link,
      { href: "/help", class: "iconlink" },
      glyph("info-sign")
    )
  )
};

interface ILogoAttributes {
  hidden?: boolean;
}

export const LeftLogo: Component<ILogoAttributes> = {
  view: (vnode) => m(".logo" + (vnode.attrs.hidden ? ".hidden" : ""),
    m(m.route.Link,
      { href: '/main', class: "nodecorate" },
      glyph("home")
    )
  )
};

export const HeaderLogo: Component<ILogoAttributes> = {
  view: (vnode) => m(".header-logo" + (vnode.attrs.hidden ? ".hidden" : ""),
    m(m.route.Link,
      {
        href: "/main",
        class: "backlink"
      },
      glyph("home")
    )
  )
};

// A nice graphical toggler control

interface ITogglerAttributes {
  id: string;
  state: boolean;
  tabindex: number;
  opt1: VnodeChildren;
  opt2: VnodeChildren;
  funcToggle?: (state: boolean) => void;
  small?: boolean;
  title?: string;
}

export const Toggler: ComponentFunc<ITogglerAttributes> = () => {

  function doToggle(togglerId: string, funcToggle?: (state: boolean) => void) {
    // Perform the toggling, on a mouse click or keyboard input (space bar)
    const cls1 = document.querySelector("#" + togglerId + " #opt1")?.classList;
    const cls2 = document.querySelector("#" + togglerId + " #opt2")?.classList;
    cls1 && cls1.toggle("selected");
    cls2 && cls2.toggle("selected");
    if (funcToggle !== undefined && cls2)
      // Toggling the switch and we have an associated function:
      // call it with the boolean state of the switch
      funcToggle(cls2.contains("selected"));
  }

  return {
    view: ({attrs: { id, small, funcToggle, state, tabindex, title, opt1, opt2 }}) => {
      const togglerId = id + "-toggler";
      const optionClass = ".option" + (small ? ".small" : "");
      return m.fragment({}, [
        m("input.checkbox." + id,
          {
            type: "checkbox",
            id: id,
            name: id,
            checked: state,
            value: 'True'
          }
        ),
        m(".toggler",
          {
            id: togglerId,
            tabindex,
            title,
            onclick: (ev: Event) => {
              doToggle(togglerId, funcToggle);
              ev.preventDefault();
            },
            onkeypress: (ev: KeyboardEvent) => {
              if (ev.key == " ") {
                doToggle(togglerId, funcToggle);
                ev.preventDefault();
              }
            }
          },
          [
            m(optionClass + (state ? "" : ".selected"), { id: "opt1" }, opt1),
            m(optionClass + (state ? ".selected" : ""), { id: "opt2" }, opt2)
          ]
        )
      ]);
    }
  };
};

export const TogglerReady: ComponentFunc<{ view: IView }> = (initialVnode) => {
  // Toggle on left-hand side of main screen:
  // User ready and willing to accept challenges

  const outerView = initialVnode.attrs.view;
  const model = outerView.model;

  function toggleFunc(state: boolean) {
    if (model.state) {
      model.state.ready = state;
      outerView.actions.setUserPref({ ready: state });
    }
  }

  return {
    view: () => m(Toggler, {
      id: "ready",
      state: model.state?.ready ?? false,
      tabindex: 2,
      opt1: nbsp(),
      opt2: glyph("thumbs-up"),
      funcToggle: toggleFunc,
      small: true,
      title: ts("Tek við áskorunum!"),
    })
  };
};

export const TogglerReadyTimed: ComponentFunc<{ view: IView }> = (initialVnode) => {
  // Toggle on left-hand side of main screen:
  // User ready and willing to accept timed challenges

  const outerView = initialVnode.attrs.view;
  const model = outerView.model;

  function toggleFunc(state: boolean) {
    if (model.state) {
      model.state.readyTimed = state;
      outerView.actions.setUserPref({ ready_timed: state });
    }
  }

  return {
    view: () => m(Toggler, {
      id: "timed",
      state: model.state?.readyTimed ?? false,
      tabindex: 3,
      opt1: nbsp(),
      opt2: glyph("time"),
      funcToggle: toggleFunc,
      small: true,
      title: ts("Til í viðureign með klukku!"),
    })
  };
};

interface IBooleanAttributes {
  view: IView;
  state: boolean;
  tabindex: number;
}

export const TogglerAudio: ComponentFunc<IBooleanAttributes> = (initialVnode) => {
  // Toggle for audio on/off
  const { model } = initialVnode.attrs.view;

  function toggleFunc(state: boolean) {
    if (state && model.state !== null) playAudio(model.state, "your-turn");
  }

  return {
    view: ({attrs: { state, tabindex }}) => m(Toggler, {
      id: "audio",
      state,
      tabindex,
      opt1: glyph("volume-off"),
      opt2: glyph("volume-up"),
      funcToggle: toggleFunc,
      small: true,
      title: ts("Hljóð á/af"),
    })
  };
};

export const TogglerFanfare: ComponentFunc<IBooleanAttributes> = (initialVnode) => {
  // Toggle for fanfare on/off
  const { model } = initialVnode.attrs.view;

  function toggleFunc(state: boolean) {
    if (state && model.state !== null) playAudio(model.state, "you-win");
  }

  return {
    view: ({attrs: { state, tabindex }}) => m(Toggler, {
      id: "fanfare",
      state,
      tabindex,
      opt1: glyph("volume-off"),
      opt2: glyph("volume-up"),
      funcToggle: toggleFunc,
      small: true,
      title: ts("Lúðraþytur á/af"),
    })
  };
};

export const TogglerBeginner: ComponentFunc<IBooleanAttributes> = () => {
  // Toggle for beginner mode, on/off

  return {
    view: ({attrs: { state, tabindex }}) => m(Toggler, {
      id: "beginner",
      state,
      tabindex,
      opt1: nbsp(),
      opt2: glyph("ok"),
      small: true,
    })
  };
};

export const TogglerFairplay: ComponentFunc<IBooleanAttributes> = () => {
  // Toggle for fairplay setting, on/off

  return {
    view: ({attrs: { state, tabindex }}) => m(Toggler, {
      id: "fairplay",
      state,
      tabindex,
      opt1: nbsp(),
      opt2: glyph("edit"),
      small: true,
    })
  };
};
